/*
 * Copyright 2021 Spotify AB
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useState, useEffect, memo, useMemo} from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useParams } from 'react-router';
import { useTaskEventStream } from '../hooks/useEventStream';
import LazyLog from 'react-lazylog/build/LazyLog';
import {
    CircularProgress,
    StepButton, StepContent,
    StepIconProps,
} from '@material-ui/core';
import { Status, TaskOutput } from '../../types';
import { DateTime, Interval } from 'luxon';
import { useInterval } from 'react-use';
import {TMNATheme} from 'tmna-theme';
import { TaskPageLinks } from './TaskPageLinks';
import {PageLayout, Content} from 'tmna-ui-kit';
import CheckIcon from "@material-ui/icons/Check";
import ClearIcon from "@material-ui/icons/Clear";
import cn from "classnames";
import { ErrorPage } from '@backstage/core-components';
import { scaffolderApiRef } from '../../api';
import { useApi } from '@backstage/core-plugin-api';

// typings are wrong for this library, so fallback to not parsing types.
const humanizeDuration = require('humanize-duration');

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    content: {
        paddingTop: theme.spacing(5),
    },
    stepper: {
      width: '240px',
    },
    taskLogger: {
      flexGrow: 1,
    },
    button: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    actionsContainer: {
      marginBottom: theme.spacing(2),
    },
    resetContainer: {
      padding: theme.spacing(3),
    },
    labelWrapper: {
      display: 'flex',
      flex: 1,
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    stepContent: {
      '& .MuiStepContent-transition': {
          paddingTop: 0,
      }
    }
  }),
);

type TaskStep = {
  id: string;
  name: string;
  status: Status;
  startedAt?: string;
  endedAt?: string;
};

const StepTimeTicker = ({ step }: { step: TaskStep }) => {
  const [time, setTime] = useState('');


  useInterval(() => {
    if (!step.startedAt) {
      setTime('');
      return;
    }

    const end = step.endedAt
      ? DateTime.fromISO(step.endedAt)
      : DateTime.local();

    const startedAt = DateTime.fromISO(step.startedAt);
    const formatted = Interval.fromDateTimes(startedAt, end)
      .toDuration()
      .valueOf();

    setTime(humanizeDuration(formatted, { round: true }));
  }, 1000);

  return <Typography variant="body2">{time}</Typography>;
};

const useStepIconStyles = makeStyles((theme: TMNATheme) =>
  createStyles({
      root: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: theme.palette.tmnaColors.secondary.slateGray,
          color: theme.palette.tmnaColors.primary.white,

          fontSize: '12px',
          lineHeight: 1,
          fontWeight: theme.typography.fontWeightBold as any,
      },
      active: {
          backgroundColor: 'transparent',
      },
      error: {
          backgroundColor: theme.palette.tmnaColors.primary.black,
      },
      coloured: {
          borderRadius: '50%',
          backgroundImage: 'linear-gradient(to bottom, #f71840, #ff9265)',
      }
  }),
);

function TaskStepIconComponent(props: StepIconProps) {
  const classes = useStepIconStyles();
  const { active, completed, error, icon: stepNumber } = props;

  const getIcon = () => {
    if (active) {
        return <CircularProgress size="24px" color="secondary" />;
    }
    if (completed) {
        return <CheckIcon fontSize="small"/>;
    }
    if (error) {
        return <ClearIcon fontSize="small"/>;
    }
    return stepNumber;
  };

  return (
    <div
      className={cn(classes.root, {
        [classes.active]: active,
        [classes.coloured]: completed,
        [classes.error]: error,
      })}
    >
      {getIcon()}
    </div>
  );
}

export const TaskStatusStepper = memo(
  ({
    steps,
    currentStepId,
    onUserStepChange,
  }: {
    steps: TaskStep[];
    currentStepId: string | undefined;
    onUserStepChange: (id: string) => void;
  }) => {
    const classes = useStyles();

    return (
      <div className={classes.root}>
        <Stepper
          activeStep={steps.findIndex(s => s.id === currentStepId)}
          orientation="vertical"
          nonLinear
        >
          {steps.map((step, index) => {
            const isCompleted = step.status === 'completed';
            const isFailed = step.status === 'failed';
            const isActive = step.status === 'processing';
            const isSkipped = step.status === 'skipped';

            return (
              <Step key={String(index)} expanded>
                <StepButton onClick={() => onUserStepChange(step.id)}>
                  <StepLabel
                    StepIconProps={{
                      completed: isCompleted,
                      error: isFailed,
                      active: isActive,
                    }}
                    StepIconComponent={TaskStepIconComponent}
                  >
                    <span>{step.name}</span>
                  </StepLabel>
                </StepButton>
                <StepContent key={step.name} className={classes.stepContent}>
                    {isSkipped ? (
                        <Typography variant="caption">Skipped</Typography>
                    ) : (
                        <StepTimeTicker step={step} />
                    )}
                </StepContent>
              </Step>
            );
          })}
        </Stepper>
      </div>
    );
  },
);

const TaskLogger = memo(({ log }: { log: string }) => {
  return (
    <div style={{ height: '80vh' }}>
      <LazyLog text={log} extraLines={1} follow selectableLines enableSearch />
    </div>
  );
});

const hasLinks = ({ entityRef, remoteUrl, links = [] }: TaskOutput): boolean =>
  !!(entityRef || remoteUrl || links.length > 0);

export const TaskPage = () => {
    const classes = useStyles();
  const [userSelectedStepId, setUserSelectedStepId] = useState<
    string | undefined
  >(undefined);
  const [lastActiveStepId, setLastActiveStepId] = useState<string | undefined>(
    undefined,
  );
  // const [testing,setTesting]=useState(true);
  const scaffolderApi = useApi(scaffolderApiRef);
  const { taskId,repo} = useParams();
  const showTroubleShootLink=repo?true:false;
  const taskStream = useTaskEventStream(taskId as any);
  const completed = taskStream.completed;
  const steps = useMemo(
    () =>
      taskStream.task?.spec.steps.map(step => ({
        ...step,
        ...taskStream?.steps?.[step.id],
      })) ?? [],
    [taskStream],
  );

  const callAction=async()=>{
    if(repo !== undefined){
      try{
        await scaffolderApi.createGithubIssue(repo);
      } catch(error){
        console.error(error);
      }
    }
  }

  useEffect(() => {
    const mostRecentFailedOrActiveStep = steps.find(step =>
      ['failed', 'processing'].includes(step.status),
    );    
   
    if (completed && !mostRecentFailedOrActiveStep) {
      setLastActiveStepId(steps[steps.length - 1]?.id);
      if(steps[steps.length - 1].status==="completed"){
      callAction();
      }
      return;
    }

    setLastActiveStepId(mostRecentFailedOrActiveStep?.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [steps, completed]);

  const currentStepId = userSelectedStepId ?? lastActiveStepId;
  


  const logAsString = useMemo(() => {
    if (!currentStepId) {
      return 'Loading...';
    }
    let log = taskStream.stepLogs[currentStepId];

    if (!log?.length) {
      return 'Waiting for logs...';
    }
    // After completion of all the steps Added custom message to user. 
    if(repo && steps[steps.length - 1].status==="completed"){
      log.join('\n');
      log=[...log,`Please wait until GitHub Action tab appears on your repo. This might take a minute.`]
      log.join('\n');
      log=[...log,`Thereafter, update getting-started.md in the docs folder and merge to master branch so as to reflect changes on Chofer Portal.`]
    }
      
    
    return log.join('\n');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskStream.stepLogs, currentStepId]);

  const taskNotFound =
    taskStream.completed === true &&
    taskStream.loading === false &&
    !taskStream.task;

  const { output } = taskStream;

  return (
  <PageLayout
      type="entity"
      title="Task Activity"
      subtitle={`Activity for task: ${taskId}`}
  >
      <Content className={classes.content}>
          {taskNotFound ? (
              <ErrorPage
                  status="404"
                  statusMessage="Task not found"
                  additionalInfo="No task found with this ID"
              />
          ) : (
              <div>
                  <Grid container>
                      <Grid item className={classes.stepper}>
                          <TaskStatusStepper
                              steps={steps}
                              currentStepId={currentStepId}
                              onUserStepChange={setUserSelectedStepId}
                          />
                          {output && hasLinks(output)? (
                              <TaskPageLinks output={output} showTroubleShootlink={showTroubleShootLink}/>
                          ):
                           (<TaskPageLinks output={[] as any} showTroubleShootlink={showTroubleShootLink}/>)}
                      </Grid>
                      <Grid item className={classes.taskLogger}>
                          <TaskLogger log={logAsString} />
                      </Grid>
                  </Grid>
              </div>
          )}
      </Content>
  </PageLayout>
  );
};
